import React from "react";

const IconAlerta = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 8v5" />
    <path d="M12 17h.01" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

function iniciales(nombre) {
  if (!nombre) return "—";
  const partes = String(nombre).replace(",", "").trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase() || "—";
}

function fechaLegible(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Cabecera fija del paciente. Siempre visible durante el scroll.
 * - `paciente`  : objeto de /listar_paciente_ultimas_citas (todos los escalares).
 * - `cita`      : objeto de /get_citas_paciente (estado, doctor, clínica, fecha).
 * - `alergias`  : array de strings (observaciones de la alergia registrada).
 *   Si NO hay antecedente de alergia registrado -> no se pinta nada (ausencia
 *   de registro no es "sin alergias"; no se inventa un chip verde).
 * - `estadoControl` : si el médico puede cambiar el estado desde aquí, se pasa
 *   un nodo; si es solo lectura, se muestra como chip.
 */
export default function CabeceraPaciente({ paciente = {}, cita = {}, alergias = [], onConsultasAnteriores, estadoControl }) {
  const nombre = paciente.nombres_completos || cita?.pacientes?.nombres_completos || "Paciente";
  const edad = paciente.edad ?? "";
  const genero = paciente.genero || "";
  const cedula = paciente.cedula || "";
  const hc = paciente.hc_msp || "";
  const seguro = cita?.pacientes?.seguros?.nombre || paciente?.seguros?.nombre || "";
  const cobertura = seguro ? seguro : "Particular";
  const estado = cita?.estado_cita?.estado || "";
  const doctor = [cita?.doctor?.prefijo, cita?.doctor?.nombre].filter(Boolean).join(" ");
  const clinica = cita?.pacientes?.clinicas?.nombre || "";

  return (
    <header className="cev__pbar">
      <span className="cev__av" aria-hidden="true">{iniciales(nombre)}</span>
      <div style={{ minWidth: 0 }}>
        <div className="cev__pname">{nombre}</div>
        <div className="cev__pmeta">
          {[edad !== "" ? `${edad} años` : null, genero, cedula ? `CI ${cedula}` : null, hc ? `HC ${hc}` : null]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>

      {alergias.length > 0 && (
        <span className="cev__tag cev__tag--red" title="Alergias registradas del paciente">
          <IconAlerta />
          Alergia · {alergias.join(", ")}
        </span>
      )}

      <span className="cev__tag cev__tag--grey">{cobertura}</span>

      {onConsultasAnteriores && (
        <button type="button" className="cev__btn cev__btn--sm" onClick={onConsultasAnteriores}>
          Consultas anteriores
        </button>
      )}

      <div className="cev__pbar-grow" />

      <div className="cev__ctx">
        <div className="cev__cf">
          <div className="l">Fecha</div>
          <div className="v">{fechaLegible(cita?.fecha_inicio)}</div>
        </div>
        <div className="cev__cf">
          <div className="l">Estado</div>
          <div className="v">
            {estadoControl || <span className="cev__tag cev__tag--blue" style={{ height: 19 }}>{estado || "—"}</span>}
          </div>
        </div>
        <div className="cev__cf">
          <div className="l">Doctor</div>
          <div className="v">{doctor || "—"}</div>
        </div>
        <div className="cev__cf">
          <div className="l">Clínica</div>
          <div className="v">{clinica || "—"}</div>
        </div>
        <div className="cev__cf">
          <div className="l">Cobertura</div>
          <div className="v">{cobertura}</div>
        </div>
      </div>
    </header>
  );
}
